import React, {ReactNode, SVGAttributes} from "react";
import {PortalDocumentInterface} from "../../../document/PortalDocument";
import {
	Annotation,
	AnnotationInterface,
	AnnotationPoint,
	AnnotationPointPoint,
	AnnotationRectangle,
	MetadataRectangle,
	Positions
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import LineStyle from "./LineStyle";
import PortalContext from "../../../PortalContext";
import Immutable from "../../../../shared/immutable/Immutable";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";

export interface AbstractAnnotationProperties extends I18nGlobals {
	pageNumber: number;
	zoomValue: number;
	context: PortalContext;
	annotation: Immutable<AnnotationInterface>;
	portalDocument: Immutable<PortalDocumentInterface>;
}

export default class AbstractAnnotation<PROPERTY_TYPE extends AbstractAnnotationProperties = any, STATUS_TYPE = any>
	extends PortalPureComponent<PROPERTY_TYPE, STATUS_TYPE> {
	protected getPoints(): Array<AnnotationPointPoint> {
		let annotation: Annotation = Annotation.fromJson(this.props.annotation.get());
		let points: Array<AnnotationPointPoint> = [];
		let positions: Array<Positions> = FunctionHelper.applyValue(
			annotation.positions, Annotation.getPositionsDefault()
		);

		for (let position of positions) {
			let positionType: AnnotationPoint = position as AnnotationPoint
			if (FunctionHelper.exists(positionType.point)) {
				points.push(positionType.point!);
			}
		}

		return points;
	}

	protected getRectangleBoundaries(): MetadataRectangle {
		let annotation: Annotation = Annotation.fromJson(this.props.annotation.get());
		let positions: Array<Positions> = FunctionHelper.applyValue(
			annotation.positions, Annotation.getPositionsDefault()
		);

		for (let position of positions) {
			let positionType: AnnotationRectangle = position as AnnotationRectangle;
			if (FunctionHelper.exists(positionType.rectangle)) {
				return positionType.rectangle!;
			}
		}

		return MetadataRectangle.fromJson({height: 0, width: 0, x: 0, y: 0});
	}

	protected getLineBoundaries(): MetadataRectangle {
		let points: Array<AnnotationPointPoint> = this.getPoints();
		let lineBoundaries: MetadataRectangle =
			MetadataRectangle.fromJson({height: 0, width: 0, x: 0, y: 0});

		for (let [index, point] of points.entries()) {
			if (index === 0) {
				lineBoundaries = MetadataRectangle.fromJson({
					x: point.x,
					y: point.y,
					width: point.x,
					height: point.y
				});
			}

			if (lineBoundaries.x! > point.x!) {
				lineBoundaries.x = point.x!;
			}

			if (lineBoundaries.y! > point.y!) {
				lineBoundaries.y = point.y!;
			}

			if (lineBoundaries.width! < point.x!) {
				lineBoundaries.width = point.x!;
			}

			if (lineBoundaries.height! < point.y!) {
				lineBoundaries.height = point.y!;
			}
		}

		return lineBoundaries;
	}

	protected renderCloudBorder(
		points: Array<AnnotationPointPoint>, width: number, stroke: string, fill: string
	): ReactNode {
		let segmentElements: Array<ReactNode> = [];

		for (let [index, point] of points.entries()) {
			let next: AnnotationPointPoint = FunctionHelper.applyValue(points[index + 1], points[0]);

			let xLength: number = next.x! - point.x!;
			let yLength: number = next.y! - point.y!;

			let segments: number =
				Math.round((Math.abs(xLength) > Math.abs(yLength) ? Math.abs(xLength) : Math.abs(yLength)) / (width * 2));
			let xOffset: number = xLength / segments;
			let yOffset: number = yLength / segments;

			for (let j: number = 0; j < segments; j++) {
				let circleX: number = point.x! + (j * xOffset);
				let circleY: number = point.y! + (j * yOffset);

				segmentElements.push(
					<circle key={segmentElements.length} cx={circleX} cy={circleY} r={width} stroke={stroke}
							strokeWidth={width} fill={fill}/>
				);
			}
		}

		return (
			<g>
				{segmentElements}
			</g>
		);
	}

	protected renderMarker(id: string, stroke: string, fill: string): ReactNode {
		let annotation: Annotation = Annotation.fromJson(this.props.annotation.get());
		let width: number = Math.max(
			FunctionHelper.exists(annotation.border) ? annotation.border!.width! / 2 : 1, 1
		);

		if (!FunctionHelper.exists(annotation.line)) {
			return null;
		}

		let markerEndAttributes: SVGAttributes<SVGMarkerElement> = {
			id: "lineEnd-" + id,
			width: 10,
			height: 10,
			refX: 0,
			refY: 5,
			orient: "auto"
		};

		let pathEndAttributes: SVGAttributes<SVGPathElement> = {
			fill: "none",
			stroke: stroke,
		};

		switch (annotation.line?.endStyle) {
			case LineStyle.OPEN_ARROW:
				markerEndAttributes.refX = 10;
				pathEndAttributes.d = "M 0 0 L 10 5, 0 10 z";
				pathEndAttributes.strokeDasharray = "0, 0, 23";
				break;
			case LineStyle.ROPEN_ARROW:
				pathEndAttributes.d = "M 0 5 L 10 0, 10 10 z";
				pathEndAttributes.strokeDasharray = "0, 0, 10.7";
				break;
			case LineStyle.CLOSED_ARROW:
				markerEndAttributes.refX = 10;
				pathEndAttributes.d = "M 0 0 L 10 5, 0 10 z";
				pathEndAttributes.fill = fill;
				break;
			case LineStyle.RCLOSED_ARROW:
				pathEndAttributes.d = "M 0 5 L 10 0, 10 10 z";
				pathEndAttributes.fill = fill;
				break;
			case LineStyle.BUTT:
				pathEndAttributes.d = "M 0 0 L 0 10 z";
				pathEndAttributes.strokeWidth = width;
				break;
			case LineStyle.SLASH:
				markerEndAttributes.refX = 5;
				pathEndAttributes.d = "M 0 10 L 10 0 z";
				pathEndAttributes.strokeWidth = width;
				break;
			case LineStyle.CIRCLE:
				markerEndAttributes.refX = 10;
				pathEndAttributes.d = "M 0 5 C 0 5, 0 0, 5 0, 5 0, 10 0, 10 5, 10 5, 10 10, 5 10, 5 10, 0 10, 0 5 z";
				pathEndAttributes.fill = fill;
				pathEndAttributes.strokeWidth = width;
				break;
			case LineStyle.SQUARE:
				markerEndAttributes.refX = 10;
				pathEndAttributes.d = "M 0 0 L 10 0, 10 10, 0 10 z";
				pathEndAttributes.fill = fill;
				pathEndAttributes.strokeWidth = width;
				break;
			case LineStyle.DIAMOND:
				markerEndAttributes.refX = 10;
				pathEndAttributes.d = "M 0 5 L 5 0, 10 5, 5 10 z";
				pathEndAttributes.fill = fill;
				pathEndAttributes.strokeWidth = width;
				break;
		}

		let markerStartAttributes: SVGAttributes<SVGMarkerElement> = {
			id: "lineStart-" + id,
			width: 10,
			height: 10,
			refX: 0,
			refY: 5,
			orient: "auto"
		};

		let pathStartAttributes: SVGAttributes<SVGPathElement> = {
			fill: "none",
			stroke: stroke,
		};

		switch (annotation.line?.startStyle) {
			case LineStyle.OPEN_ARROW:
				pathStartAttributes.d = "M 0 5 L 10 0, 10 10 z";
				pathStartAttributes.strokeDasharray = "0, 0, 10.7";
				break;
			case LineStyle.ROPEN_ARROW:
				markerStartAttributes.refX = 10;
				pathStartAttributes.d = "M 0 0 L 10 5, 0 10 z";
				pathStartAttributes.strokeDasharray = "0, 0, 23";
				break;
			case LineStyle.CLOSED_ARROW:
				pathStartAttributes.d = "M 0 5 L 10 0, 10 10 z";
				pathStartAttributes.fill = fill;
				break;
			case LineStyle.RCLOSED_ARROW:
				markerStartAttributes.refX = 10;
				pathStartAttributes.d = "M 0 0 L 10 5, 0 10 z";
				pathStartAttributes.fill = fill;
				break;
			case LineStyle.BUTT:
				pathStartAttributes.d = "M 0 0 L 0 10 z";
				pathStartAttributes.strokeWidth = width;
				break;
			case LineStyle.SLASH:
				markerStartAttributes.refX = 5;
				pathStartAttributes.d = "M 0 10 L 10 0 z";
				pathStartAttributes.strokeWidth = width;
				break;
			case LineStyle.CIRCLE:
				pathStartAttributes.d = "M 0 5 C 0 5, 0 0, 5 0, 5 0, 10 0, 10 5, 10 5, 10 10, 5 10, 5 10, 0 10, 0 5 z";
				pathStartAttributes.fill = fill;
				pathStartAttributes.strokeWidth = width;
				break;
			case LineStyle.SQUARE:
				pathStartAttributes.d = "M 0 0 L 10 0, 10 10, 0 10 z";
				pathStartAttributes.fill = fill;
				pathStartAttributes.strokeWidth = width;
				break;
			case 'Diamond':
				pathStartAttributes.d = "M 0 5 L 5 0, 10 5, 5 10 z";
				pathStartAttributes.fill = fill;
				pathStartAttributes.strokeWidth = width;
				break;
		}

		return (
			<defs>
				<marker {...markerEndAttributes}>
					<path {...pathEndAttributes}/>
				</marker>
				<marker {...markerStartAttributes}>
					<path {...pathStartAttributes}/>
				</marker>
			</defs>
		);
	}
}